import { useParentPosition } from "@/hooks/useParentPosition";
import { useTranslations, useLocale } from "next-intl";
import { Fragment, useId, useRef, useState, useEffect, useCallback, useMemo } from "react";
import { PInfo } from "../PInfo/PInfo";
import { OverflowTooltip } from "../TableCell/OverflowTooltip";
import { sortOptionsForDisplay } from "@/lib/utils/sortOptionsForDisplay";
import {
  IconChevronDown,
  IconCircle,
  IconCircleCheckFilled,
} from "@tabler/icons-react";
import { HelpHint } from "../HelpHint/HelpHint";
import { hasHover } from "@/lib/utils/pointer";
import { motion } from "framer-motion";
import { HOVER_QUERY, SM_UP_QUERY, useMediaQuery } from "@/hooks/useMediaQuery";
import {
  SHEET_ANIMATE,
  SHEET_EXIT,
  SHEET_INITIAL,
  SHEET_SPRING,
} from "@/components/Modal/sheetMotion";


type OptionInputProps = {
  /** DOM id for the input. Also wires the visible `label` to it via `htmlFor`,
   *  which is how this picker gets an accessible name without `ariaLabel` —
   *  the label used to be a bare `<label>` bound to nothing. Opt-in: with no
   *  `id` both attributes are omitted, so every other caller's DOM is
   *  unchanged. */
  id?: string;
  /** Optional external ref. When omitted, the component falls back to its
   *  own internal ref (line 100). External refs are still required by some
   *  callers that need focus/blur control from the parent. */
  inputRef?: React.RefObject<HTMLInputElement | null>;
  border?: string;
  isFilter?: boolean;
  options: string[];
  defaultValue: string;
  required: boolean;
  setParent?: (value: string) => void;
  onFocusAction?: () => void;
  height?: string;
  surface?: string;
  fontSize?: string;
  padding?: string;
  editMode?: boolean;
  label?: string;
  rounded?: string;
  hoverMessage?: string;
  // Delayed "what is this?" hint shown as an info icon beside the label —
  // matches the guest-detail popup (appears only after the pointer rests).
  info?: string;
  width?: string;
  labelMargin?: string;
  /** Label scale. Default matches FormInput's, so no existing caller moves —
   *  this exists because the label size used to be HARDCODED here, which made
   *  a select's label a different size from the text field next to it in the
   *  same grid row. The detail popups pass `body-3-medium` (guest parity). */
  labelFontSize?: string;
  onBlurAction?: (value: string) => void;
  updateOptions?: boolean;
  error?: string;
  errorPlaceholder?: boolean;
  disabled?: boolean;
  nothingFoundText?: string;
  commitOnSelectOnly?: boolean;
  optionLabels?: Record<string, string>;
  /** Optional per-option colors (option value -> hex) — renders a color dot
   *  in the dropdown rows. Used by colored SINGLE_SELECT custom fields. */
  optionColors?: Record<string, string>;
  /** When true, the dropdown collapses entirely when filtering yields zero
   *  matches. Used by directory pickers where "no matches" should hide the
   *  suggestion list (instead of showing the "Nothing found" placeholder). */
  hideWhenEmpty?: boolean;
  /** When true, display the dropdown rows in locale-aware alphabetical order
   *  (by the resolved label — see `optionLabels`). Opt-in: most callers feed
   *  options whose order is deliberate (operators, times, status funnels,
   *  relevance-ranked directories) and must be preserved. Only user-defined
   *  SELECT-field option lists turn this on. */
  sortOptions?: boolean;
  /** Closed-list mode: the value MUST be one of `options`. Typing only filters
   *  (it never commits), and on blur the input either resolves to an exact
   *  option (matched by raw value or displayed label) or snaps back to the last
   *  valid value — so a half-typed search can never persist as the value.
   *  Opt-in; used by SINGLE_SELECT cells over a fixed option set. */
  strictOption?: boolean;
  /** Optional, ordered semantic sections for the dropdown. When provided, the
   *  filtered recommendations are rendered under non-selectable group headers
   *  (each group shows only the recommendations its `match` predicate accepts;
   *  any unmatched leftovers render headerless at the end so a row can never be
   *  dropped). A group with an EMPTY label renders its rows headerless at its
   *  position — used to pin sentinel entries (e.g. an export's "(None)" row)
   *  ahead of the labeled sections. Absent → flat list, byte-for-byte
   *  unchanged. Purely visual: filtering / blur / closed-list resolution still
   *  operate on `options`. */
  optionGroups?: { label: string; match: (option: string) => boolean }[];
  /** Per-option hint (keyed by the option string): a short `label` rendered
   *  muted beside the option, and an optional `example` appended to the row's
   *  hover title. Used by the operator picker to explain each glyph. */
  optionHints?: Record<string, { label: string; example?: string }>;
  /** Options that are FULL (per-option capacity reached) and not the current
   *  value — rendered greyed + non-selectable. The current value is never in
   *  this set, so an already-chosen full option is never locked out. */
  disabledOptions?: string[];
  /** Drop the trailing chevron (e.g. the compact operator glyph cell). */
  hideChevron?: boolean;
  /** Ghost text shown when the field is empty (e.g. an unpicked condition field
   *  select), so it doesn't read as a broken blank box. */
  placeholder?: string;
  /** Accessible name for the input when there is no visible `label` (e.g. the
   *  compact social-type picker whose meaning is carried by its row). */
  ariaLabel?: string;
  /** Native maxLength on the input. Needed by free-text (non-strict) callers
   *  with a backend length cap: clamping only in setParent leaves the
   *  DISPLAYED text unclamped (the defaultValue prop stops changing once the
   *  clamp saturates, so the sync effect never re-seeds) — the browser-level
   *  cap keeps display and committed state identical. */
  maxLength?: number;
  /** Focus (and thereby open) this input once on mount — used by a freshly
   *  added condition row so the field picker is ready without an extra click. */
  autoFocus?: boolean;
  testId?: string;
  /** Report a pick of the value ALREADY shown through `setParent` (the list
   *  still closes). Off by default: a re-pick is no change for a form with a
   *  current value. On for a host where the pick itself is the decision — the
   *  bulk device editor, where "No category" on a fresh dialog means "clear
   *  the category on every selected device". */
  notifySamePick?: boolean;
  /** Keep focus on the field after a pick (the list closes) instead of
   *  blurring it. Off by default — every existing host relies on the pick
   *  ending the edit. On for a field inside a small modal dialog whose next
   *  stop is its own buttons: the blur dropped focus to <body>, and WebKit's
   *  next Tab then left the dialog for the page behind it. The commit is the
   *  pick itself either way; the later real blur resolves to the same value. */
  keepFocusOnPick?: boolean;
};

/**
 * Swallow the next `click` on `target`, once — the click that the press just
 * stopped at document capture would otherwise still reach the host's own
 * `onClick` (a scrim closes on click, not on mousedown). Armed outside any
 * effect's lifetime; disarms on that click or after a moment.
 */
function armClickSwallow(target: EventTarget): void {
  let timer: number | undefined;
  const onClick = (e: Event) => {
    if (e.target === target) {
      e.stopPropagation();
      e.preventDefault();
    }
    disarm();
  };
  const disarm = () => {
    document.removeEventListener("click", onClick, true);
    if (timer !== undefined) window.clearTimeout(timer);
  };
  document.addEventListener("click", onClick, true);
  timer = window.setTimeout(disarm, 1000);
}

export const OptionInput: React.FC<OptionInputProps> = ({
  id,
  inputRef: externalInputRef,
  errorPlaceholder,
  border = "outline-tertiary focus:border focus:outline-focus-bam border focus:outline-hidden",
  options,
  defaultValue,
  isFilter,
  required,
  setParent,
  notifySamePick = false,
  keepFocusOnPick = false,
  onFocusAction,
  height = "h-9",
  surface = "",
  fontSize = "body-2-regular",
  editMode = false,
  padding = "px-2 py-1",
  label,
  rounded = "rounded-[10px]",
  hoverMessage,
  info,
  width,
  labelMargin = "mb-2",
  labelFontSize = "body-2-medium",
  onBlurAction,
  updateOptions = false,
  error,
  disabled = false,
  nothingFoundText,
  commitOnSelectOnly = false,
  optionLabels,
  optionColors,
  hideWhenEmpty = false,
  sortOptions = false,
  strictOption = false,
  optionGroups,
  optionHints,
  disabledOptions,
  hideChevron = false,
  placeholder,
  ariaLabel,
  maxLength,
  autoFocus = false,
  testId,
}) => {
  const t = useTranslations("inputColumnComponent");
  const disabledSet = new Set(disabledOptions ?? []);
  const locale = useLocale();
  const isMouseDownOnDropdown = useRef(false);
  const [showRecommendations, setShowRecommendations] = useState(false);
  const [inputValue, setInputValue] = useState(defaultValue || "");
  const [inputWidth, setInputWidth] = useState<number>(0);
  const [positionReady, setPositionReady] = useState(false);

  useEffect(() => {
    setInputValue(defaultValue || "");
    inputValueRef.current = defaultValue || "";
  }, [defaultValue]);

  const inputValueRef = useRef(defaultValue || "");
  const [recommendations, setRecommendations] = useState<string[]>(options);

  useEffect(() => {
    if (!updateOptions) return;
    // Re-apply the active filter when the parent supplies a new options
    // list. Without this, an async-loaded option list would always render
    // the full set even after the user has typed a search — defeating
    // substring filtering for directory pickers.
    const search = (inputValueRef.current || "").toLowerCase();
    if (!search) {
      setRecommendations(options);
      return;
    }
    setRecommendations(
      options.filter((option) => {
        if (option.toLowerCase().includes(search)) return true;
        if (optionLabels?.[option]?.toLowerCase().includes(search)) return true;
        return false;
      }),
    );
  }, [options, updateOptions, optionLabels]);

  const [showTooltip, setShowTooltip] = useState(false);
  const {
    dropdownPosition: dropdownPositionLabel,
    setDropdownPosition: setDropdownPositionLabel,
  } = useParentPosition();
  const labelRef = useRef<HTMLLabelElement>(null);

  const { dropdownPosition, setDropdownPosition } = useParentPosition();

  const internalInputRef = useRef<HTMLInputElement>(null);
  const inputRef = externalInputRef || internalInputRef;

  /**
   * On a phone a STRICT picker stops being a text field and becomes a button
   * that opens a sheet.
   *
   * Anchoring a list to a field while the software keyboard is up turned out to
   * be a moving target: iOS changes the layout viewport, the visual viewport,
   * the document scroll and the frame `position: fixed` resolves against, all
   * at once and not in step. Three separate causes were found and fixed there
   * and the list still landed in the wrong place. A sheet has no anchor and
   * raises no keyboard, so there is nothing left to compute or to cover.
   *
   * EVERY picker gets the sheet, not just the strict ones. The custom-field
   * selects in the guest form are `strictOption={false}` — they accept free
   * text — and they are exactly the ones that were reported as misplaced, so a
   * gate on strictness would have missed the case it was built for.
   *
   * What strictness still decides is the keyboard: a field that accepts only
   * listed options has nothing to type and becomes readOnly, so no keyboard
   * appears at all. A free-text combo keeps its keyboard, and the sheet then
   * rests on top of it via `bandInset` instead of hiding behind it.
   */
  const isSmUp = useMediaQuery(SM_UP_QUERY);
  const sheetMode = !isSmUp && !editMode;
  /**
   * A mouse or trackpad. Decides whether the open list needs a full-screen
   * SHIELD under it: on touch it does (the shield is what carries `touch-none`,
   * so a swipe that starts outside the list scrolls nothing), on a pointer
   * device it must not exist at all.
   *
   * The shield used to be unconditional, and it swallowed the first click
   * anywhere else on the page: pressing it blurred the input, the blur closed
   * the list, the shield unmounted between mousedown and mouseup, and the
   * browser then resolved the click against a detached node — so the button the
   * user actually aimed at never fired. Measured in Chrome against the seating
   * furniture dialog: with the list open, `elementFromPoint` over Save returned
   * the shield, and the first click did not save. Closing is not its job
   * anyway — the input's own blur already does that.
   */
  const hasPointerHover = useMediaQuery(HOVER_QUERY);

  // Focus once on mount when requested (freshly added condition row). Deferred a
  // tick so the portal-positioned dropdown measures against a laid-out input.
  useEffect(() => {
    if (!autoFocus) return;
    const id = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // FAILSAFE 1: Guarantee position is calculated before dropdown renders
  useEffect(() => {
    if (showRecommendations && inputRef.current) {
      setDropdownPosition(inputRef);
      setInputWidth(inputRef.current.offsetWidth);
    }
  }, [showRecommendations]);

  // FAILSAFE 2: Update position on scroll/resize while dropdown is open —
  // and, for the anchored list, on every frame.
  //
  // Scroll and resize are not the only ways the FIELD moves under an open
  // list. The auto-seat dialog is centred and grows by a refusal list once a
  // plan arrives; its head, and the table picker in it, slide up by half the
  // growth. No event fires for that, and the list — `fixed` at a `top`
  // measured before the growth — stayed ~220px below the field. A
  // ResizeObserver on the dialog would need every host to opt in; the field's
  // own rect is the one truth, so it is read once per frame while the list is
  // open. `useParentPosition` bails out on unchanged values, so an idle frame
  // renders nothing. The phone sheet is not anchored and is left out.
  //
  // Test trap: a self-rescheduling rAF never drains. With `requestAnimationFrame`
  // faked, `vi.runAllTimers()` aborts at its loop limit while a list is open —
  // step the clock with `advanceTimersByTime`/`advanceTimersToNextFrame`
  // instead.
  useEffect(() => {
    if (showRecommendations && inputRef.current) {
      const updatePosition = () => {
        if (inputRef.current) {
          setDropdownPosition(inputRef);
        }
      };

      // Use capture phase to catch all scroll events
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);
      // iOS raises the software keyboard without firing a window resize — only
      // the visual viewport changes, and the list would keep its stale anchor.
      window.visualViewport?.addEventListener("resize", updatePosition);
      window.visualViewport?.addEventListener("scroll", updatePosition);

      let frame = 0;
      // Not in `editMode` either: a table cell never renders the list, so a
      // frame loop there would measure for nothing.
      if (!sheetMode && !editMode) {
        const tick = () => {
          updatePosition();
          frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
      }

      return () => {
        window.removeEventListener("scroll", updatePosition, true);
        window.removeEventListener("resize", updatePosition);
        window.visualViewport?.removeEventListener("resize", updatePosition);
        window.visualViewport?.removeEventListener("scroll", updatePosition);
        cancelAnimationFrame(frame);
      };
    }
  }, [showRecommendations, sheetMode, editMode]);

  // Wait for position to be calculated before marking ready
  useEffect(() => {
    if (showRecommendations && dropdownPosition.top !== 0) {
      setPositionReady(true);
    }
  }, [showRecommendations, dropdownPosition]);



  // FAILSAFE 3: Centralized dropdown opening function
  const openDropdown = useCallback(() => {
    if (disabled || editMode) return;

    // Always set position first
    if (inputRef.current) {
      setDropdownPosition(inputRef);
      setInputWidth(inputRef.current.offsetWidth);
    }

    // Then show dropdown
    setRecommendations(options);
    if (onFocusAction) onFocusAction();
    setPositionReady(false);
    setShowRecommendations(true);
  }, [disabled, editMode, options, onFocusAction]);

  const handleKeyInputChange = (value: string) => {
    if (disabled) return;

    if (!showRecommendations) {
      // FAILSAFE 4: Set position before showing dropdown when typing
      if (inputRef.current) {
        setDropdownPosition(inputRef);
      }
      setPositionReady(false);
      setShowRecommendations(true);
      if (onFocusAction) onFocusAction();
    }

    let newRecommendations = options.filter((option) => {
      const search = (value || "").toLowerCase();
      if (option.toLowerCase().includes(search)) return true;
      if (optionLabels?.[option]?.toLowerCase().includes(search)) return true;
      return false;
    });

    if (value.trim()?.length === 0) newRecommendations = options;

    setInputValue(value);
    inputValueRef.current = value;
    setRecommendations(newRecommendations);

    if (setParent && !commitOnSelectOnly && !strictOption) setParent(value);
  };

  const handleRecommendationClick = (value: string) => {
    if (disabled) return;

    // Re-picking the value it already has is not a change, so nothing is
    // committed — but it IS a dismissal. It used to return with the list still
    // open, which read as a dead click and left the user a second click away
    // from the dialog underneath.
    if (value === inputValue) {
      if (notifySamePick && setParent) setParent(value);
      setShowRecommendations(false);
      setPositionReady(false);
      if (keepFocusOnPick) return;
      // Arm the swallow before the explicit blur — see the pick below.
      isMouseDownOnDropdown.current = true;
      inputRef.current?.blur();
      return;
    }

    // A full (capacity-reached) option can't be picked. The current value is
    // never in disabledSet, so re-selecting your own choice stays possible.
    if (disabledSet.has(value)) return;

    inputValueRef.current = value;
    setInputValue(value);
    setRecommendations([]);
    setShowRecommendations(false);
    setPositionReady(false);
    if (setParent) setParent(value);
    if (onBlurAction) onBlurAction(value);
    // The pick IS the commit; the blur that follows must not resolve again.
    // It used to be a no-op by accident: the native blur had already happened
    // at mousedown (focus went to <body>) and was swallowed by the flag. Now
    // the list keeps focus on the input, the flag is disarmed a task later,
    // and a human-speed click (mousedown, a task, click) arrives with the flag
    // down — so this `.blur()` ran the FULL handler synchronously, before the
    // pick was committed: a free-text field called `onBlurAction` a second
    // time with the STALE DOM value (the language picker reverted the pick),
    // a strict field committed twice. Arm the flag, and the handler swallows
    // it exactly as it swallowed the native one.
    // `keepFocusOnPick`: no blur at all — focus stays on the field with the
    // list closed (a mouse pick never moved it: the list's mousedown is
    // prevented), so there is nothing to swallow either.
    if (keepFocusOnPick) return;
    isMouseDownOnDropdown.current = true;
    inputRef.current?.blur();
  };

  // Alphabetical display order, opt-in via `sortOptions`. Sort a copy of the
  // *filtered* recommendations (the `options` prop stays canonical so
  // options[0] blur-autofill and validation keep their original semantics).
  // Sorting on the resolved label via optionLabels means raw-value fields
  // (language/timeZone: value "en", label "English") order by what the user
  // reads, not by the code.
  const sortedRecommendations = useMemo(
    () =>
      sortOptions
        ? sortOptionsForDisplay(
            recommendations,
            optionLabels ? (o) => optionLabels[o] ?? o : undefined,
            locale,
          )
        : recommendations,
    [sortOptions, recommendations, optionLabels, locale],
  );

  /**
   * The rows in the order they are PAINTED, plus the sections they are painted
   * in. Arrow keys walk `ordered`, so it has to be the same sequence the eye
   * follows — with `optionGroups` that is section by section, leftovers last,
   * which is not the order of `sortedRecommendations`. Computing it twice
   * would let the highlight and the list drift apart the first time a group
   * predicate changed.
   */
  const { sections, leftovers, ordered } = useMemo(() => {
    if (!optionGroups || optionGroups.length === 0) {
      return { sections: null, leftovers: [], ordered: sortedRecommendations };
    }
    const matched = new Set<string>();
    const secs = optionGroups.map((group) => {
      const rows = sortedRecommendations.filter(
        (o) => !matched.has(o) && group.match(o),
      );
      rows.forEach((o) => matched.add(o));
      return { label: group.label, rows };
    });
    const rest = sortedRecommendations.filter((o) => !matched.has(o));
    return {
      sections: secs,
      leftovers: rest,
      ordered: [...secs.flatMap((sec) => sec.rows), ...rest],
    };
  }, [optionGroups, sortedRecommendations]);

  /**
   * Is a list actually ON SCREEN?
   *
   * `showRecommendations` only says the field is open; `hideWhenEmpty` can
   * still suppress the whole popup, and the two used to disagree everywhere it
   * mattered — the chevron flipped to "open" over nothing, `aria-expanded`
   * announced a popup a screen reader could not reach, and Escape was swallowed
   * by an invisible list while the dialog behind it stayed put. One derived
   * truth, used by every one of those.
   *
   * `positionReady` is deliberately NOT part of it: that is a transient
   * measurement of the anchored list, not a statement about visibility, and
   * folding it in would make the chevron stutter on every open.
   */
  const listVisible =
    showRecommendations && !(hideWhenEmpty && sortedRecommendations.length === 0);

  /**
   * Once the phone sheet is up it stays up while the field is focused. Gating
   * the sheet itself on `listVisible` made its dimmed backdrop blink on and off
   * with every keystroke that crossed the "matches nothing" line.
   */
  const sheetOpenRef = useRef(false);
  if (!showRecommendations) sheetOpenRef.current = false;
  else if (listVisible) sheetOpenRef.current = true;
  const sheetVisible = showRecommendations && sheetOpenRef.current;

  /**
   * Keyboard highlight. `-1` means "nothing active" — an arrow key is what
   * activates the first row, so a stray Enter can never commit a row the user
   * never looked at. Shape taken from the house pattern in
   * `PAddVariable/TemplateVariableList` (activeIndex at -1, reset on filter,
   * clamped movement, `scrollIntoView?.({block:"nearest"})`).
   *
   * This is a REPLACEMENT for a capability the native `<datalist>` had and
   * this picker never did: arrow keys moved through the browser's suggestion
   * popup, and every field migrated onto OptionInput lost that.
   */
  const [activeIndex, setActiveIndex] = useState(-1);

  /**
   * A field that becomes `disabled` while its list is open CLOSES the list.
   *
   * The render gate below already hides the list of a disabled field, but
   * hiding is not closing: `showRecommendations` stayed true, and none of the
   * places that set it false hang on `disabled`. The seat dialog greys its
   * table picker while a plan loads — the focus trap had just focused the
   * picker on mount, so the list was open and measured against a dialog that
   * was still small. `onBlur` returns early on a disabled field and focus went
   * to <body>. Then the dialog grew by a refusal list, the picker slid up, the
   * plan came back, `disabled` fell — and the list reappeared at its old
   * coordinates with nothing able to close it: no focused input for Escape or
   * the next blur. Every host that passes `disabled` passes a busy flag around
   * a write, so closing is the right answer for all of them, and the next
   * focus opens and measures afresh.
   */
  useEffect(() => {
    if (!disabled) return;
    setShowRecommendations(false);
    setPositionReady(false);
    setActiveIndex(-1);
  }, [disabled]);
  const listboxId = `${useId()}-listbox`;
  const optionDomId = (i: number) => `${listboxId}-opt-${i}`;
  const listRef = useRef<HTMLUListElement>(null);
  /** The viewport-spanning positioning box around the anchored list (and the touch shield). */
  const boxRef = useRef<HTMLDivElement>(null);

  /**
   * A press on a dialog's SCRIM while this list is open belongs to this list.
   *
   * <p>The old click shield swallowed such presses for every host by accident
   * (it lived inside the host's subtree). Without it, the press that closes
   * the list also reaches the host's backdrop and closes the dialog — for
   * many hosts a discard of everything typed. The marker rule
   * (`data-press-owner`, read by the shells and `useBackdropDismiss`) needs
   * every host to opt in, and the re-audit found ~44 dialogs painting their
   * own scrim that had not: the dominant `fixed left-0 top-0 h-dvh w-screen`
   * shape was invisible to the scanner that was supposed to keep them honest.
   *
   * <p>So the rule is enforced HERE, once, for every host present and future:
   * while the anchored list is visible, a press whose target is a
   * viewport-spanning `position: fixed` element outside this field — a scrim,
   * by construction; a button is never that — is stopped at document capture
   * (before any React handler, so `onMouseDown`, `onMouseDownCapture` and the
   * document-level outside-press listeners never see it), the list closes,
   * and the `click` the same press produces on the same target is swallowed
   * once. The input still blurs (focus moves to the scrim), so a strict field
   * resolves as it always did. A press on anything else — Save, a row, the
   * page — is untouched: one click on Save stays one click.
   */
  useEffect(() => {
    if (!listVisible || sheetMode || editMode || disabled) return;
    if (typeof document === "undefined") return;
    const isViewportScrim = (el: Element): boolean => {
      if (typeof getComputedStyle !== "function") return false;
      if (getComputedStyle(el).position !== "fixed") return false;
      const r = el.getBoundingClientRect();
      return r.width >= window.innerWidth - 1 && r.height >= window.innerHeight - 1;
    };
    const onPress = (e: Event) => {
      const target = e.target;
      if (!(target instanceof Element)) return;
      if (boxRef.current?.contains(target)) return;
      if (inputRef.current?.contains(target) || inputRef.current === target) return;
      if (target.closest("[data-optioninput-field]") === inputRef.current?.closest("[data-optioninput-field]")
          && target.closest("[data-optioninput-field]") !== null) return;
      if (!isViewportScrim(target)) return;
      e.stopPropagation();
      // The click this press produces lands AFTER the list has closed — and
      // closing it re-runs this effect, whose cleanup would have removed a
      // listener registered here. So the one-shot click swallow is armed on
      // its own, outside the effect's lifetime, and disarms itself on the
      // click or after a moment (a press that produces no click: a drag).
      armClickSwallow(target);
      setShowRecommendations(false);
      setPositionReady(false);
    };
    document.addEventListener("pointerdown", onPress, true);
    document.addEventListener("mousedown", onPress, true);
    return () => {
      document.removeEventListener("pointerdown", onPress, true);
      document.removeEventListener("mousedown", onPress, true);
    };
    // `inputRef` is a ref; `setShowRecommendations`/`setPositionReady` are
    // stable setters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listVisible, sheetMode, editMode, disabled]);

  // A keystroke that re-filters invalidates the highlight: index 2 of the old
  // list is a different option in the new one. So does a list that changed
  // under the highlight without a keystroke — `updateOptions` callers refresh
  // their options while the list is open (the furniture dialog recomputes its
  // categories on every store refresh), and an index kept across that would
  // let Enter commit a row the user never arrowed to.
  const orderedKey = ordered.join("\u0000");
  useEffect(() => setActiveIndex(-1), [inputValue, showRecommendations, orderedKey]);

  useEffect(() => {
    if (activeIndex < 0) return;
    // jsdom implements neither scrollIntoView nor CSS.escape on every version —
    // both are optional-called for that reason (same as the house pattern).
    listRef.current
      ?.querySelector(`#${CSS.escape(optionDomId(activeIndex))}`)
      ?.scrollIntoView?.({ block: "nearest" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex]);

  /** A row the user can actually land on — greyed rows are skipped, except the
   *  one that is already the value (which the click path also lets through). */
  const selectable = (option: string) =>
    !disabledSet.has(option) || option === inputValue;

  /** Move the highlight `delta` rows, clamped at both ends, stepping over
   *  greyed rows in the direction of travel. Nothing selectable that way =
   *  stay put, rather than wrapping around behind the user's back. */
  const moveActive = (delta: number) => {
    if (!listVisible || ordered.length === 0) return;
    const from = activeIndex;
    let next =
      from < 0 ? (delta > 0 ? 0 : ordered.length - 1) : from + delta;
    next = Math.max(0, Math.min(next, ordered.length - 1));
    const step = delta > 0 ? 1 : -1;
    while (next >= 0 && next < ordered.length && !selectable(ordered[next])) {
      next += step;
    }
    if (next < 0 || next >= ordered.length) return;
    setActiveIndex(next);
  };

  /** Home / End: the first (or last) row the user can land on. */
  const jumpActive = (toEnd: boolean) => {
    if (!listVisible || ordered.length === 0) return;
    const idx = toEnd
      ? [...ordered].reverse().findIndex(selectable)
      : ordered.findIndex(selectable);
    if (idx < 0) return;
    setActiveIndex(toEnd ? ordered.length - 1 - idx : idx);
  };

  // Single source for an option row — reused by the flat list and the grouped
  // (optionGroups) render so both stay visually identical.
  const renderOptionRow = (option: string) => {
    const isSelected = inputValue === option;
    const isFull = disabledSet.has(option) && !isSelected;
    const hint = optionHints?.[option];
    // Position in the PAINTED order, which is what the arrow keys walk.
    const index = ordered.indexOf(option);
    const isActive = index >= 0 && index === activeIndex;
    return (
      <li
        key={`option-${option}`}
        id={index >= 0 ? optionDomId(index) : undefined}
        role="option"
        aria-selected={isSelected}
        data-testid={testId ? `${testId}-option-${option}` : undefined}
        aria-disabled={isFull || undefined}
        // The keyboard highlight is its own visual state: `aria-selected`
        // already means "this is the value", so the active row cannot borrow
        // the selected row's surface without saying something untrue.
        className={`py-1.5 rounded-lg flex items-center justify-between gap-2 ${
          isFull
            ? "opacity-40 cursor-not-allowed font-quaternary"
            : isSelected
              ? "surface-tertiary font-primary cursor-default"
              : "font-quaternary cursor-pointer hover:surface-tertiary hover:font-primary"
        } ${isActive && !isFull ? "surface-tertiary font-primary" : ""} body-3-regular px-2`}
        onClick={() => handleRecommendationClick(option)}
      >
        {optionColors?.[option] && (
          <span
            className="h-2.5 w-2.5 shrink-0 rounded-full border outline-primary"
            style={{ backgroundColor: optionColors[option] }}
          />
        )}
        {hint ? (
          // Hovering the option (a beat longer) shows the styled help bubble
          // explaining the glyph — no extra icon, rows stay clean.
          <HelpHint
            message={hint.example ? `${hint.label} — ${hint.example}` : hint.label}
          >
            <span className="flex-1 min-w-0 truncate">
              {optionLabels?.[option] || option}
            </span>
          </HelpHint>
        ) : (
          <OverflowTooltip
            text={optionLabels?.[option] || option}
            className="flex-1 min-w-0"
          >
            {optionLabels?.[option] || option}
          </OverflowTooltip>
        )}
        {isFull ? (
          <span className="shrink-0 body-3-regular font-quaternary">
            {t("optionFull")}
          </span>
        ) : isSelected ? (
          <IconCircleCheckFilled size={14} className="font-primary shrink-0" />
        ) : (
          <IconCircle size={14} className="font-quaternary shrink-0" />
        )}
      </li>
    );
  };

  /**
   * The option rows, built once and rendered by BOTH presentations: the
   * anchored list on a pointer device, and the bottom sheet on a phone. They
   * have to stay identical — grouping, hints, the "nothing found" line — and a
   * second copy would drift the first time a row grows a feature.
   */
  const optionRows =
    ordered.length > 0 ? (
      sections ? (
        // Grouped render: each section shows the recommendations its predicate
        // matches; any leftover renders headerless at the end. The sections
        // themselves are computed once, with `ordered`, so the arrow keys and
        // the eye walk the same sequence.
        <>
          {sections
            .map((section, i) => ({ ...section, i }))
            .filter((sec) => sec.rows.length > 0)
            .map((section) => (
              <Fragment key={`group-${section.i}-${section.label}`}>
                {section.label && (
                  // A listbox may only contain options, so a header opts out
                  // of the role hierarchy rather than pretending to be one.
                  <li
                    role="presentation"
                    className="pt-1 px-2 body-4-bold font-quinary cursor-default"
                  >
                    {section.label}
                  </li>
                )}
                {section.rows.map(renderOptionRow)}
              </Fragment>
            ))}
          {leftovers.map(renderOptionRow)}
        </>
      ) : (
        <>{ordered.map(renderOptionRow)}</>
      )
    ) : (
      <li
        role="presentation"
        className="py-8 text-center font-quaternary body-3-regular"
      >
        {nothingFoundText ? nothingFoundText : t("noFieldsFound")}
      </li>
    );

  return (
    <div className={`flex flex-col ${width}`}>
      {showTooltip && hoverMessage && (
        <PInfo message={hoverMessage} position={dropdownPositionLabel} />
      )}
      {label && (
        <HelpHint message={info}>
          <label
            ref={labelRef}
            htmlFor={id}
            onMouseEnter={() => {
              if (hoverMessage) {
                setShowTooltip(true);
                setDropdownPositionLabel(labelRef);
              }
            }}
            onMouseLeave={() => setShowTooltip(false)}
            className={`${labelMargin} inline-block w-max max-w-full font-secondary ${labelFontSize} ${
              hoverMessage ? "cursor-help" : ""
            } ${disabled ? "opacity-50" : ""}`}
          >
            {label}
          </label>
        </HelpHint>
      )}
      <div
        // Lifted above the dropdown's own outside-click catcher while the list
        // is open. That catcher is a full-screen `fixed inset-0` sheet, and it
        // covered THIS field too: the first click opened the list, the second
        // click of a double-click landed on the catcher, and the word under the
        // cursor was never selected. Measured with `elementFromPoint` at the
        // field's centre — the INPUT while closed, the catcher while open.
        //
        // Safe because the list opens on FOCUS, not on click: a click that now
        // reaches the field again does not re-toggle anything, it just puts the
        // caret where the user aimed. Everything outside the field is still
        // covered, so clicking away still closes.
        // The surface belongs on the BOX, not only on the <input> inside it:
        // with it painted on the input alone, the 10px corners and the strip
        // under the border showed whatever was behind the field. FormInput
        // paints its wrapper for exactly this reason. The input keeps its own
        // copy, so nothing about a caller's `surface` changes.
        className={`relative w-full ${height} ${border} ${rounded} outline-quaternary ${
          surface || "surface-primary"
        } ${listVisible && !sheetMode ? "z-30" : ""}`}
      >
        <input
          id={id}
          ref={inputRef}
          value={
            // Prefer the translated label whenever the current inputValue
            // is a known option key — both when the dropdown is closed and
            // while it's open (so users with `optionLabels` set don't see
            // the raw key flash on focus). Typing overwrites `inputValue`
            // with the typed string, which won't match any key, so the
            // typeahead fallback to `inputValue` still works.
            optionLabels?.[inputValue] ?? inputValue
          }
          autoComplete="off"
          // A readOnly input still focuses (which is what opens the list) but
          // raises no keyboard on iOS — the whole point of the sheet.
          readOnly={sheetMode && strictOption}
          disabled={disabled}
          placeholder={placeholder}
          data-testid={testId}
          role="combobox"
          // A combobox has to name the popup it drives, say what kind of
          // autocomplete it is, and point at the active row — otherwise a
          // screen reader hears "combobox, expanded" with nothing to go to.
          // `aria-controls` only while the list EXISTS: a dangling IDREF is
          // itself an a11y error (same rule as MultiOptionInput).
          aria-controls={listVisible ? listboxId : undefined}
          aria-autocomplete={strictOption ? "list" : "both"}
          aria-activedescendant={
            listVisible && activeIndex >= 0 ? optionDomId(activeIndex) : undefined
          }
          aria-label={ariaLabel}
          maxLength={maxLength}
          // `name` mirrors `id` the way FormInput does, so a field swapped
          // between the two keeps the same handle.
          name={id}
          aria-expanded={listVisible}
          // Its own Escape (stopPropagation) cannot beat a capture-phase host —
          // the marker is what makes such a host stand down. It is also what
          // tells a host shell that a child owns THIS dismiss gesture, so a
          // backdrop press that closes the list must not also tear the host
          // down (see ModalWrapper / Modal / PSeatCellEditor).
          data-escape-owner={listVisible ? "true" : undefined}
          // The PRESS twin, read by backdrops and outside-press hosts on
          // mousedown. Separate from the Escape marker on purpose — see
          // `lib/a11y/dismissOwner.ts`.
          data-press-owner={listVisible ? "true" : undefined}
          onMouseEnter={() => !disabled && hasHover() && setDropdownPosition(inputRef)}
          onChange={(e) => {
            handleKeyInputChange(e.target.value);
          }}
          onFocus={() => {
            if (disabled || editMode) return;

            // Use centralized opening function
            openDropdown();
          }}
          onBlur={() => {
            if (disabled) return;

            if (isMouseDownOnDropdown.current) {
              isMouseDownOnDropdown.current = false;
              return;
            }

            if (strictOption) {
              const typed = inputValueRef.current.trim();
              const prior = defaultValue || "";
              // Resolve the typed text to a real option — matched by its raw
              // value (e.g. language code "en-GB") or its displayed label
              // (e.g. "English (United Kingdom)").
              const matched = typed
                ? options.find(
                    (option) =>
                      option === inputValueRef.current ||
                      (optionLabels?.[option] ?? option).toLowerCase() ===
                        typed.toLowerCase(),
                  )
                : undefined;
              // A greyed option is non-selectable by TYPING as well: the row
              // click refused it from the start, but a typed label resolved to
              // it here and committed a chair the list showed as taken. (A
              // greyed CURRENT value needs no exemption: unresolved snaps back
              // to `prior`, which is that value.)
              const resolved =
                matched !== undefined && !disabledSet.has(matched) ? matched : undefined;
              // Decide the value the field settles on:
              //  - exact match  → that option
              //  - emptied+required → first SELECTABLE option (prior autofill behavior)
              //  - emptied+optional → unset
              //  - partial/invalid/greyed → snap back to the last valid value
              const next = resolved
                ? resolved
                : typed === "" && required && options.length > 0
                  ? (options.find((o) => !disabledSet.has(o)) ?? options[0])
                  : typed === ""
                    ? ""
                    : prior;
              setInputValue(next);
              inputValueRef.current = next;
              // Commit only on a real change — opening the field and tabbing
              // away without picking must not re-fire (matches the non-strict
              // path and avoids redundant updates).
              if (next !== prior) {
                if (setParent) setParent(next);
                if (onBlurAction) onBlurAction(next);
              }
              setShowRecommendations(false);
              setPositionReady(false);
              return;
            }

            const currentValue = inputValueRef.current.trim();
            const validField = options.find(
              (option) => option === inputValueRef.current,
            );

            if (options.length > 0) {
              if (!validField) {
                // Only auto-fill on blur if:
                // 1. Field is required
                // 2. Field is completely empty
                // 3. User has finished editing (blur, not during typing)
                if (required && currentValue === "") {
                  const newValue = options[0];
                  setInputValue(newValue);
                  inputValueRef.current = newValue;
                  if (setParent) setParent(newValue);
                  if (onBlurAction) onBlurAction(newValue);
                } else if (!required && currentValue === "") {
                  const newValue = "";
                  setInputValue(newValue);
                  inputValueRef.current = newValue;
                  if (setParent) setParent(newValue);
                  if (onBlurAction) onBlurAction(newValue);
                } else if (currentValue !== "") {
                  // User has typed something invalid - let them see the error
                  // Don't auto-fill, but do trigger validation
                  if (onBlurAction) onBlurAction(currentValue);
                }
              } else if (validField) {
                if (onBlurAction) onBlurAction(inputRef.current?.value || "");
              }
            }

            setShowRecommendations(false);
            setPositionReady(false);
          }}
          onKeyDown={(e) => {
            if (disabled) return;

            // Escape with the dropdown open closes ONLY the dropdown — it
            // must not bubble into an enclosing form/modal cancel handler
            // and discard the user's edits.
            if (e.key === "Escape" && listVisible) {
              e.stopPropagation();
              setShowRecommendations(false);
              setPositionReady(false);
              setActiveIndex(-1);
              return;
            }

            // Arrow keys walk the list. The native <datalist> these pickers
            // replaced could do this; OptionInput could not, so every field
            // moved onto it silently lost its keyboard. `preventDefault` keeps
            // Up/Down from jumping the caret to the ends of the text.
            if (e.key === "ArrowDown" || e.key === "ArrowUp") {
              if (!listVisible) {
                // Closed field: open it the way focus does, and let the next
                // press move. Matches the native popup's behaviour.
                e.preventDefault();
                openDropdown();
                return;
              }
              e.preventDefault();
              moveActive(e.key === "ArrowDown" ? 1 : -1);
              return;
            }
            if ((e.key === "Home" || e.key === "End") && listVisible && activeIndex >= 0) {
              // Only once the user is IN the list: with nothing highlighted,
              // Home/End belong to the text field's caret.
              e.preventDefault();
              jumpActive(e.key === "End");
              return;
            }

            if (e.key === "Enter") {
              // Modifier+Enter is a form-level shortcut (Cmd/Ctrl+Enter =
              // save) — committing a pick here AND letting the event bubble
              // would make the form save the pre-pick state (both handlers
              // run in one dispatch; React batches the setParent update).
              if (e.metaKey || e.ctrlKey) return;
              e.preventDefault();
              // A highlighted row is an explicit choice — commit it. With
              // nothing highlighted the old behaviour stands unchanged
              // (strict: accept the top filtered match; free text: just blur),
              // so a stray Enter still cannot pick a row on its own.
              if (listVisible && activeIndex >= 0 && ordered[activeIndex]) {
                handleRecommendationClick(ordered[activeIndex]);
                return;
              }
              // Strict fields: when the user has typed a partial search that
              // isn't already a valid option, Enter accepts the top filtered
              // match. With nothing typed (or the current value shown), Enter
              // just closes — so a stray Enter never jumps to the first option.
              if (strictOption) {
                const typed = inputValueRef.current.trim();
                const alreadyValid = options.some(
                  (o) =>
                    o === typed ||
                    (optionLabels?.[o] ?? o).toLowerCase() ===
                      typed.toLowerCase(),
                );
                if (typed !== "" && !alreadyValid) {
                  const top = sortedRecommendations.find((o) => o !== "—");
                  // A greyed top row is not skipped for the next one — typing
                  // "2" over a greyed "Seat 2" must not quietly take "Seat 12".
                  // Falling through blurs, and blur snaps the field back.
                  if (top && !disabledSet.has(top)) {
                    handleRecommendationClick(top);
                    return;
                  }
                }
              }
              inputRef.current?.blur();
            }
          }}
          className={`${padding} surface-primary w-full focus:outline-hidden ${surface} h-full ${rounded} ${fontSize} ${
            !editMode && !hideChevron ? "pr-8" : ""
          } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
        />
        {!editMode && !hideChevron && (
          <IconChevronDown
            size={16}
            className={`absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none transition-transform duration-200 ${
              listVisible ? "rotate-180" : ""
            } ${disabled ? "opacity-50" : ""}`}
          />
        )}
      </div>
      {/* A phone gets the sheet; everything else keeps the anchored list. The
          sheet needs no `positionReady` — there is no position to wait for,
          which is exactly why it exists. */}
      {!editMode && !disabled && sheetVisible && sheetMode && (
        <div
          // Covers exactly the visible strip, given BOTH edges. The `bottom`
          // this replaced went negative with the keyboard up — the layout
          // viewport is shorter than the document offset by then — and pushed
          // the sheet down behind the keyboard instead of onto it.
          // The geometry GLIDES. iOS reports the shrinking viewport in steps
          // while the keyboard slides in, and each step moves this box — the
          // sheet flew up, got recomputed, and dropped back. The jumps are not
          // avoidable (the keyboard's height is unknowable until it is there),
          // so they are animated instead of hidden.
          className="fixed inset-x-0 z-20 flex items-end bg-black/50 sm:pr-[var(--ai-rail-push)] transition-[top,height] duration-200 ease-out motion-reduce:transition-none"
          style={{
            top: `${dropdownPosition.bandTop}px`,
            height: `${dropdownPosition.bandHeight}px`,
          }}
          onClick={(e) => {
            e.stopPropagation();
            setShowRecommendations(false);
            setPositionReady(false);
          }}
        >
          <motion.div
            // `70%` of the BACKDROP, which is sized to the visible band — not `70dvh`.
            // `dvh` is the viewport with the browser's toolbars collapsed, the
            // largest it ever gets, so a dvh cap overflows its own container
            // whenever a toolbar is actually out. That is why the height looked
            // wrong in Safari and right in Brave: their bars sit differently.
            className="w-full surface-secondary rounded-t-3xl shadow-lg flex flex-col max-h-[70%] overflow-hidden"
            initial={SHEET_INITIAL}
            animate={SHEET_ANIMATE}
            exit={SHEET_EXIT}
            transition={SHEET_SPRING}
            onClick={(e) => e.stopPropagation()}
            // Same guard the anchored list uses: the field blurs the moment a
            // finger lands here, and without this the strict-option resolution
            // in `onBlur` would run before the tap has chosen anything.
            onPointerDown={() => {
              isMouseDownOnDropdown.current = true;
            }}
            onMouseDown={() => {
              isMouseDownOnDropdown.current = true;
            }}
          >
            {label && (
              <p className="px-4 pt-4 pb-1 shrink-0 body-2-medium font-secondary">
                {label}
              </p>
            )}
            <ul
              ref={listRef}
              id={listboxId}
              role="listbox"
              aria-label={ariaLabel ?? label ?? undefined}
              className="p-2 space-y-1 overflow-y-auto overscroll-contain"
            >
              {optionRows}
            </ul>
          </motion.div>
          {/* The sheet's colour, continued below the strip we believe is
              visible. Absolutely positioned and OUTSIDE the panel, so it adds
              no layout: an earlier attempt did this with padding plus a
              negative margin, which moved the panel itself down and pushed the
              options off the screen.

              Whatever we get wrong about where the keyboard starts is covered
              by this rather than showing a band of undimmed page, and with no
              keyboard it falls off the screen unseen. Deliberately generous and
              deliberately not measured — the one number here that must not be
              exact. */}
          <div
            aria-hidden
            className="absolute inset-x-0 top-full h-80 surface-secondary"
          />
        </div>
      )}
      {/* hideWhenEmpty (folded into `listVisible`): collapse the dropdown
          entirely when there are no matches — the caller asked us to stay
          silent rather than render the "nothing found" placeholder. */}
      {!editMode && !disabled && listVisible && !sheetMode && positionReady && (
        <div
          // `pointer-events-none`: this box spans the viewport only so the list
          // inside it can be positioned against the viewport — it is not a
          // surface. While it WAS one it swallowed the first click anywhere
          // else on the page (see `hasPointerHover`), so the list's own
          // children re-enable pointer events and nothing else here does.
          className="fixed z-20 inset-0 h-dvh w-screen pointer-events-none"
          ref={boxRef}
        >
          <ul
            ref={listRef}
            id={listboxId}
            role="listbox"
            // `overscroll-contain`: scrolling the option list to its end must
            // not hand the rest of the gesture to the page underneath — the
            // list is `fixed` at a computed position and does not travel with
            // it, so the background sliding away leaves the list pointing at
            // nothing.
            className="pointer-events-auto fixed p-1 z-20 mt-1 surface-secondary border outline-tertiary shadow-lg rounded-lg space-y-1 overflow-y-auto overscroll-contain"
            style={{
              ...(dropdownPosition.showAbove
                ? {
                    bottom: `${dropdownPosition.top}px`,
                    left: `${dropdownPosition.left}px`,
                  }
                : {
                    top: `${dropdownPosition.top}px`,
                    left: `${dropdownPosition.left}px`,
                  }),
              maxHeight: `${dropdownPosition.maxHeight}px`,
              // Anchor at least to the input's width, but let the list grow to
              // fit its option labels — a very narrow field (e.g. gender/
              // language squeezed into a 3-col row) would otherwise pin the
              // dropdown so tight no option text is readable. Capped at 20rem
              // so a long label can't sprawl, and never past the viewport's
              // right edge so the wider list never spills offscreen.
              minWidth: `${inputWidth}px`,
              width: "max-content",
              maxWidth: `min(20rem, calc(100vw - ${dropdownPosition.left}px - 16px))`,
            }}
            // The popup is named after the field: a `listbox` without an
            // accessible name is an ARIA error, and the input's own label is
            // the only honest one.
            aria-label={ariaLabel ?? label ?? undefined}
            // pointerdown fires with touchstart — BEFORE the input blurs —
            // whereas the synthesized mousedown arrives after it. Without this
            // the list unmounted mid-tap and the click had no target, so a
            // finger could never pick an option.
            onPointerDown={() => {
              isMouseDownOnDropdown.current = true;
            }}
            // `preventDefault`: a mousedown's default action is to MOVE FOCUS,
            // and a press on anything in the list that is not an option row —
            // a group header, the "nothing found" row, the padding, the gap
            // between rows, the scrollbar — moved it to <body>. The input
            // blurred, the blur was swallowed by the flag below (as designed
            // for a row click), and the list was left open with nothing able
            // to close it: no focused input for Escape or the next blur, and
            // no shield any more to click. While stuck it also kept every
            // host's backdrop from closing. Keeping focus on the input makes
            // the next outside press blur it, as always.
            //
            // The flag is still armed (a browser may blur anyway — nothing here
            // asks it not to for a touch) and disarmed on the next macrotask:
            // a blur caused by this press has fired by then, and a flag left
            // armed by a press that caused no blur would swallow the NEXT one.
            onMouseDown={(e) => {
              e.preventDefault();
              isMouseDownOnDropdown.current = true;
              window.setTimeout(() => { isMouseDownOnDropdown.current = false; }, 0);
            }}
          >
            {optionRows}
          </ul>
          {/* TOUCH ONLY. `touch-none` so a swipe that starts anywhere outside
              the list scrolls NOTHING: this shield covers the whole screen
              while the list is open, and it is not a scroller itself, so
              without it the gesture passed straight through to whatever was
              behind — the field scrolled away while its own dropdown stayed
              put. `touch-action` needs a real hit target, so the shield cannot
              be `pointer-events-none`; that is precisely why it must not exist
              for a mouse, where it ate the first click on everything else in
              the dialog.

              Deliberately on the shield and not on its parent: the option list
              is a SIBLING of this element, and `touch-action` intersects down
              the hit-test chain, so `none` on the shared parent would take the
              list's own scrolling with it. */}
          {!hasPointerHover && (
            <div
              className="pointer-events-auto fixed inset-0 h-screen w-screen touch-none"
              onClick={(e) => {
                if (!isFilter) e.stopPropagation();
                setShowRecommendations(false);
                setPositionReady(false);
              }}
            />
          )}
        </div>
      )}
      {error && <p className="mt-1 body-3-regular font-error">{error}</p>}
      {errorPlaceholder && <p className="mt-1 body-3-regular opacity-0">x</p>}
    </div>
  );
};
